import {
  Fragment,
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import {
  Combobox as WkCombobox,
  ComboboxSuggestion,
  type FormFieldErrorInfo,
} from '@wk/components-react16';

import './Combobox.css';
interface Option {
  label: ReactNode;
  value: string;
  valueLabel: string;
}

interface ComboBoxProps {
  options: Option[];
  label: string;
  disabled?: boolean;
  placeholder: string;
  labelFor: string;
  value: string;
  onChange: (value: string) => void;
  onSearchChange?: (value: string) => void;
  errors?: FormFieldErrorInfo[];
  filter?: (value: string, search: string) => boolean;
  className?: string;
  hasLoadingIcon?: boolean;
}

const ComboBox = ({
  options,
  label,
  placeholder,
  labelFor,
  errors,
  value,
  onChange,
  onSearchChange,
  filter,
  disabled,
  hasLoadingIcon,
  className,
}: ComboBoxProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const selectedOption = options.find((option) => option.value === value);

  const getFilteredOptions = useCallback(
    (search: string) =>
      options.filter((option) => {
        if (filter) {
          return filter(option.value, search);
        }
        const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedSearch, 'i');
        return regex.test(option.label?.toString() || '');
      }),
    [filter, options],
  );

  useEffect(() => {
    setFilteredOptions(getFilteredOptions(selectedOption ? '' : inputValue));
  }, [getFilteredOptions, inputValue, selectedOption]);

  const handleDropdownUserRequest = (_isOpen: boolean) => {
    setIsOpen(_isOpen);
    if (!_isOpen) {
      setHighlightedIndex(-1);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    onSearchChange?.(newValue);
    
    if (selectedOption && value) {
      onChange('');
    }
    
    const filteredList = getFilteredOptions(newValue);
    setFilteredOptions(filteredList);
    setHighlightedIndex(-1);
    
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        if (filteredOptions.length === 0) return;
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : 0
        );
        break;
      case 'ArrowUp':
        if (filteredOptions.length === 0) return;
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter': {
        if (filteredOptions.length === 0) return;
        event.preventDefault();
        const selectedIndex = highlightedIndex >= 0 ? highlightedIndex : 0;
        const optionToSelect = filteredOptions[selectedIndex];
        onChange(optionToSelect.value);
        onSearchChange?.('');
        setIsOpen(false);
        setInputValue(optionToSelect.valueLabel);
        setHighlightedIndex(-1);
        break;
      }
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'Backspace':
      case 'Delete':
        if (selectedOption && inputValue === '') {
          event.preventDefault();
          onChange('');
          setFilteredOptions(options);
          setHighlightedIndex(-1);
          setIsOpen(true);
        }
        break;
    }
  };

  const handleClearUserRequest = () => {
    onChange('');
    onSearchChange?.('');
    setInputValue('');
    setFilteredOptions(options);
    setHighlightedIndex(-1);
  };

  return (
    <div className={className}>
      <WkCombobox
        isOpen={isOpen}
        hasClearButton={!!selectedOption}
        label={label}
        hasLoadingIcon={hasLoadingIcon}
        isDisabled={disabled}
        labelFor={labelFor}
        errors={errors}
        onDropdownUserRequest={handleDropdownUserRequest}
        onClearUserRequest={handleClearUserRequest}
      >
        <input
          placeholder={placeholder}
          name={`${label}-search`}
          value={selectedOption?.valueLabel || inputValue}
          id={`${label}-search`}
          aria-labelledby={`${label}-search`}
          // aria-disabled={this.state.isDisabled}
          onFocus={() => {
            setIsOpen(true);
          }}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          slot="comboboxInput"
        />
        {filteredOptions.map((item, index) => {
          return (
            <Fragment key={`${label}-${item.value}`}>
              <ComboboxSuggestion
                isSelected={index === highlightedIndex}
                onItemClick={() => {
                  onChange(item.value);
                  onSearchChange?.('');
                  setIsOpen(false);
                  setHighlightedIndex(-1);
                }}
                slot="comboboxSuggestion"
              >
                {item.label}
              </ComboboxSuggestion>
            </Fragment>
          );
        })}
      </WkCombobox>
    </div>
  );
};

export default ComboBox;
